(function () {
  // Prevent double initialization.
  if (window.ShopifyUTMCapture && window.ShopifyUTMCapture._installed) {
    return;
  }

  const STORAGE_KEY = 'shopify_utm_attribution';
  const COOKIE_KEY = 'shopify_utm_attribution';

  // Last-touch attribution lasts 24 hours; a newer marketing visit replaces it.
  const ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  const ATTRIBUTION_TTL_SECONDS = ATTRIBUTION_TTL_MS / 1000;

  const UTM_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'gclid',
    'fbclid'
  ];

  const LINE_ITEM_ATTRIBUTION_KEYS = UTM_FIELDS.reduce(function (keys, field) {
    return keys.concat([field, `_${field}`]);
  }, []);

  const ROUTE_ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${ROUTE_ROOT}cart.js`;
  const CART_UPDATE_URL = `${ROUTE_ROOT}cart/update.js`;
  const CART_CHANGE_URL = `${ROUTE_ROOT}cart/change.js`;

  const PRODUCT_FORM_SELECTOR = [
    'form[action*="/cart/add"]',
    'form[data-product-form]'
  ].join(',');

  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href*="/checkout"]',

    // Superstore / Tenderbites selectors.
    '[data-atc-button-checkout]',
    '.atc-button--checkout',
    '.cart-title-button',

    // Generic fallbacks.
    '.cart__checkout-button',
    '.checkout-button'
  ].join(',');

  const CART_ACTION_SELECTOR = [
    'button[name="add"]',

    // Superstore Add to Cart.
    '[data-product-atc]',
    '.product-form--atc-button',

    // Generic / Dawn fallback.
    '.product-form__submit',

    // Cart update and remove.
    'cart-remove-button',
    '.cart-remove-button',
    '[data-cart-remove]',
    '[data-cart-quantity]',
    '[data-cart-update]',
    '.cart-item__remove',
    '.cart-remove'
  ].join(',');

  const BUY_NOW_SELECTOR = [
    // Shopify dynamic checkout / Buy It Now.
    '.shopify-payment-button__button',
    'shopify-buy-it-now-button button',
    'shopify-accelerated-checkout button'
  ].join(',');

  let isInternalCartUpdate = false;
  let scheduledSyncTimer = null;
  let scheduledExpiryTimer = null;
  let staleCartCleanupPromise = null;

  function getStorage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      console.warn('[UTM Capture] Session storage is not available:', error);
      return null;
    }
  }

  function getCookie(name) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const prefix = `${name}=`;

    for (let index = 0; index < cookies.length; index += 1) {
      if (cookies[index].indexOf(prefix) === 0) {
        return decodeURIComponent(cookies[index].substring(prefix.length));
      }
    }

    return '';
  }

  function setCookie(name, value, maxAgeSeconds) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      'path=/',
      `max-age=${maxAgeSeconds}`,
      'SameSite=Lax',
      secure
    ].join('; ');
  }

  function deleteCookie(name) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = [
      `${name}=`,
      'path=/',
      'max-age=0',
      'SameSite=Lax',
      secure
    ].join('; ');
  }

  function hasAttributionValue(attribution) {
    return UTM_FIELDS.some(function (field) {
      return Boolean(attribution && attribution[field]);
    });
  }

  function isExpiredAttribution(attribution) {
    if (!attribution) {
      return true;
    }

    const expiryTime = getAttributionExpiryTime(attribution);

    return !expiryTime || Date.now() >= expiryTime;
  }

  function isValidAttribution(attribution) {
    return Boolean(
      attribution &&
      typeof attribution === 'object' &&
      hasAttributionValue(attribution) &&
      !isExpiredAttribution(attribution)
    );
  }

  function getAttributionExpiryTime(attribution) {
    if (!attribution) {
      return 0;
    }

    if (
      attribution.expires_at !== undefined &&
      attribution.expires_at !== null &&
      attribution.expires_at !== ''
    ) {
      const numericExpiry = Number(attribution.expires_at);

      if (Number.isFinite(numericExpiry) && numericExpiry > 0) {
        return numericExpiry;
      }

      const parsedExpiry = new Date(attribution.expires_at).getTime();

      if (Number.isFinite(parsedExpiry) && parsedExpiry > 0) {
        return parsedExpiry;
      }
    }

    if (!attribution.captured_at) {
      return 0;
    }

    const capturedTime = new Date(attribution.captured_at).getTime();

    return Number.isFinite(capturedTime)
      ? capturedTime + ATTRIBUTION_TTL_MS
      : 0;
  }

  function scheduleAttributionExpiryCleanup(attribution) {
    window.clearTimeout(scheduledExpiryTimer);
    scheduledExpiryTimer = null;

    const expiryTime = getAttributionExpiryTime(attribution);

    if (!expiryTime) {
      return;
    }

    const remainingTime = expiryTime - Date.now();

    if (remainingTime <= 0) {
      forceClearNonUtmState().catch(function (error) {
        console.warn('[UTM Capture] Expired attribution cleanup failed:', error);
      });
      return;
    }

    scheduledExpiryTimer = window.setTimeout(function () {
      const currentAttribution = getStoredAttribution();

      if (currentAttribution) {
        scheduleAttributionExpiryCleanup(currentAttribution);
        return;
      }

      forceClearNonUtmState().catch(function (error) {
        console.warn('[UTM Capture] Scheduled attribution cleanup failed:', error);
      });
    }, remainingTime + 50);
  }

  function normalizeAttribution(attribution) {
    const capturedAtTime = attribution.captured_at
      ? new Date(attribution.captured_at).getTime()
      : Date.now();

    const validCapturedAtTime = Number.isFinite(capturedAtTime)
      ? capturedAtTime
      : Date.now();

    const expiryTime = getAttributionExpiryTime(attribution);

    return {
      ...attribution,
      captured_at:
        attribution.captured_at || new Date(validCapturedAtTime).toISOString(),
      expires_at: expiryTime || validCapturedAtTime + ATTRIBUTION_TTL_MS
    };
  }

  function getAttributionFromSessionStorage() {
    const storage = getStorage();

    if (!storage) {
      return null;
    }

    try {
      const storedValue = storage.getItem(STORAGE_KEY);
      const attribution = storedValue ? JSON.parse(storedValue) : null;

      if (!isValidAttribution(attribution)) {
        storage.removeItem(STORAGE_KEY);
        return null;
      }

      return attribution;
    } catch (error) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  function getAttributionFromCookie() {
    try {
      const rawCookie = getCookie(COOKIE_KEY);

      if (!rawCookie) {
        return null;
      }

      const attribution = JSON.parse(rawCookie);

      if (!isValidAttribution(attribution)) {
        deleteCookie(COOKIE_KEY);
        return null;
      }

      return attribution;
    } catch (error) {
      deleteCookie(COOKIE_KEY);
      return null;
    }
  }

  function saveAttribution(data) {
    const attribution = normalizeAttribution(data);
    const storage = getStorage();
    const serializedAttribution = JSON.stringify(attribution);

    if (storage) {
      try {
        storage.setItem(STORAGE_KEY, serializedAttribution);
      } catch (error) {
        console.warn('[UTM Capture] Unable to save attribution to sessionStorage:', error);
      }
    }

    try {
      setCookie(COOKIE_KEY, serializedAttribution, ATTRIBUTION_TTL_SECONDS);
    } catch (error) {
      console.warn('[UTM Capture] Unable to save attribution to cookie:', error);
    }

    scheduleAttributionExpiryCleanup(attribution);
  }

  function getCapturedAtTime(attribution) {
    if (!attribution || !attribution.captured_at) {
      return 0;
    }

    const capturedAtTime = new Date(attribution.captured_at).getTime();

    return Number.isFinite(capturedAtTime) ? capturedAtTime : 0;
  }

  function getStoredAttribution() {
    const sessionAttribution = getAttributionFromSessionStorage();
    const cookieAttribution = getAttributionFromCookie();

    if (!sessionAttribution && !cookieAttribution) {
      return null;
    }

    // Compare both stores so the newest touch wins across tabs.
    const latestAttribution =
      getCapturedAtTime(cookieAttribution) >
      getCapturedAtTime(sessionAttribution)
        ? cookieAttribution
        : sessionAttribution || cookieAttribution;

    saveAttribution(latestAttribution);

    return latestAttribution;
  }

  function clearStoredAttribution() {
    window.clearTimeout(scheduledExpiryTimer);
    scheduledExpiryTimer = null;

    const storage = getStorage();

    if (storage) {
      try {
        storage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('[UTM Capture] Unable to clear attribution from sessionStorage:', error);
      }
    }

    deleteCookie(COOKIE_KEY);
  }

  function getAttributionFromUrl() {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    const hasMarketingParameter = UTM_FIELDS.some(function (field) {
      const value = searchParams.get(field);

      return searchParams.has(field) && Boolean(value && value.trim());
    });

    if (!hasMarketingParameter) {
      return null;
    }

    const attribution = {};

    UTM_FIELDS.forEach(function (field) {
      attribution[field] = (searchParams.get(field) || '').trim();
    });

    attribution.captured_at = new Date().toISOString();
    attribution.expires_at = Date.now() + ATTRIBUTION_TTL_MS;
    attribution.landing_page = window.location.href;
    attribution.referrer = document.referrer || '';
    attribution.had_items = false;

    return attribution;
  }

  function removeMarketingParamsFromCurrentUrl() {
    if (!window.history || typeof window.history.replaceState !== 'function') {
      return;
    }

    const url = new URL(window.location.href);
    let hasChanged = false;

    UTM_FIELDS.forEach(function (field) {
      if (url.searchParams.has(field)) {
        url.searchParams.delete(field);
        hasChanged = true;
      }
    });

    if (!hasChanged) {
      return;
    }

    // Keeps preview_theme_id and other non-UTM params.
    const cleanUrl = `${url.pathname}${url.search}${url.hash}`;

    window.history.replaceState(window.history.state, document.title, cleanUrl);
  }

  function getCartAttributePayload(attribution) {
    const attributes = {};

    UTM_FIELDS.forEach(function (field) {
      attributes[field] = attribution[field] || '';
    });

    return attributes;
  }

  function getEmptyCartAttributePayload() {
    const attributes = {};

    UTM_FIELDS.forEach(function (field) {
      attributes[field] = '';
    });

    return attributes;
  }

  async function getCart() {
    const response = await fetch(CART_URL, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  function hasDifferentAttributes(currentAttributes, nextAttributes) {
    return Object.keys(nextAttributes).some(function (key) {
      return String(currentAttributes[key] || '') !== String(nextAttributes[key] || '');
    });
  }

  function hasAttributionCartAttributes(attributes) {
    return UTM_FIELDS.some(function (field) {
      return Boolean(attributes && attributes[field]);
    });
  }

  function hasAttributionLineItemProperties(item) {
    const properties = item && item.properties;

    if (!properties || typeof properties !== 'object') {
      return false;
    }

    return LINE_ITEM_ATTRIBUTION_KEYS.some(function (key) {
      return Object.prototype.hasOwnProperty.call(properties, key);
    });
  }

  function getAttributionFreeLineItemProperties(item) {
    const currentProperties = item && item.properties;
    const cleanedProperties = {};

    if (currentProperties && typeof currentProperties === 'object') {
      Object.keys(currentProperties).forEach(function (key) {
        const isAttributionProperty = LINE_ITEM_ATTRIBUTION_KEYS.includes(key);

        if (!isAttributionProperty) {
          cleanedProperties[key] = currentProperties[key];
        }
      });
    }

    // Shopify can't swap non-empty properties for {}, so keep one private marker.
    if (Object.keys(cleanedProperties).length === 0) {
      cleanedProperties._attribution_cleared = 'expired';
    }

    return cleanedProperties;
  }

  async function updateCartAttributes(attributes) {
    const cart = await getCart();
    const currentAttributes = cart.attributes || {};

    if (!hasDifferentAttributes(currentAttributes, attributes)) {
      return cart;
    }

    isInternalCartUpdate = true;

    try {
      const response = await fetch(CART_UPDATE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            ...currentAttributes,
            ...attributes
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Cart attribute update failed: ${response.status}`);
      }

      return response.json();
    } finally {
      isInternalCartUpdate = false;
    }
  }

  async function syncAttributionToCart() {
    const attribution = getStoredAttribution();

    if (!attribution) {
      return null;
    }

    syncUtmInputsToProductForms();

    return updateCartAttributes(getCartAttributePayload(attribution));
  }

  async function clearCartAttributionAttributes() {
    return updateCartAttributes(getEmptyCartAttributePayload());
  }

  async function clearLineItemAttributionProperties(initialCart) {
    let cart = initialCart || (await getCart());
    let cleanupCount = 0;
    const maxCleanupCount = Math.max((cart.items || []).length * 2, 10);

    while (cleanupCount < maxCleanupCount) {
      const staleLineIndex = (cart.items || []).findIndex(function (item) {
        return hasAttributionLineItemProperties(item);
      });

      if (staleLineIndex === -1) {
        return cart;
      }

      const staleItem = cart.items[staleLineIndex];
      const cleanedProperties = getAttributionFreeLineItemProperties(staleItem);

      isInternalCartUpdate = true;

      try {
        const response = await fetch(CART_CHANGE_URL, {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            line: staleLineIndex + 1,
            quantity: staleItem.quantity,
            properties: cleanedProperties
          })
        });

        if (!response.ok) {
          throw new Error(`Cart line-item cleanup failed: ${response.status}`);
        }

        cart = await response.json();
      } finally {
        isInternalCartUpdate = false;
      }

      cleanupCount += 1;
    }

    throw new Error('Cart line-item cleanup exceeded its safety limit.');
  }

  async function clearStaleCartAttributionAttributesIfNeeded() {
    if (getStoredAttribution()) {
      return;
    }

    if (staleCartCleanupPromise) {
      return staleCartCleanupPromise;
    }

    staleCartCleanupPromise = (async function () {
      try {
        let cart = await getCart();
        const currentAttributes = cart.attributes || {};

        if (hasAttributionCartAttributes(currentAttributes)) {
          cart = await clearCartAttributionAttributes();
        }

        if ((cart.items || []).some(hasAttributionLineItemProperties)) {
          await clearLineItemAttributionProperties(cart);
        }
      } catch (error) {
        console.warn('[UTM Capture] Unable to clear stale cart attribution:', error);
      } finally {
        staleCartCleanupPromise = null;
      }
    })();

    return staleCartCleanupPromise;
  }

  async function forceClearNonUtmState() {
    clearStoredAttribution();
    clearUtmInputsFromProductForms();
    await clearStaleCartAttributionAttributesIfNeeded();
  }

  function getInputByName(form, name) {
    return Array.from(form.querySelectorAll('input')).find(function (input) {
      return input.name === name;
    });
  }

  function addOrUpdateHiddenInput(form, name, value) {
    let input = getInputByName(form, name);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value || '';
  }

  function removeHiddenInputByName(form, name) {
    const inputs = Array.from(form.querySelectorAll('input')).filter(function (input) {
      return input.name === name;
    });

    inputs.forEach(function (input) {
      input.remove();
    });
  }

  function clearUtmInputsFromProductForms() {
    const productForms = document.querySelectorAll(PRODUCT_FORM_SELECTOR);

    productForms.forEach(function (form) {
      UTM_FIELDS.forEach(function (field) {
        removeHiddenInputByName(form, `properties[_${field}]`);
      });
    });
  }

  function syncUtmInputsToProductForms() {
    const attribution = getStoredAttribution();

    if (!attribution) {
      clearUtmInputsFromProductForms();
      return;
    }

    const productForms = document.querySelectorAll(PRODUCT_FORM_SELECTOR);

    productForms.forEach(function (form) {
      UTM_FIELDS.forEach(function (field) {
        // Line-item property fallback for Buy It Now.
        addOrUpdateHiddenInput(form, `properties[_${field}]`, attribution[field] || '');
      });
    });
  }

  function captureLatestUtmFromUrl() {
    const latestAttribution = getAttributionFromUrl();

    if (!latestAttribution) {
      const storedAttribution = getStoredAttribution();

      syncUtmInputsToProductForms();

      if (!storedAttribution) {
        clearStaleCartAttributionAttributesIfNeeded().catch(function (error) {
          console.warn('[UTM Capture] Stale cart cleanup failed:', error);
        });
      }

      return storedAttribution;
    }

    // Any marketing parameter starts a fresh snapshot; touches are never merged.
    saveAttribution(latestAttribution);
    syncUtmInputsToProductForms();

    // Strip only our marketing parameters from the URL; preview_theme_id stays.
    removeMarketingParamsFromCurrentUrl();

    syncAttributionToCart().catch(function (error) {
      console.warn('[UTM Capture] Initial cart sync failed:', error);
    });

    return latestAttribution;
  }

  function debounce(callback, delay) {
    let timer;

    return function () {
      clearTimeout(timer);

      timer = setTimeout(function () {
        callback();
      }, delay);
    };
  }

  function scheduleCartStateCheck(delays) {
    const checkDelays = delays || [300, 1000, 2500];

    checkDelays.forEach(function (delay) {
      window.setTimeout(function () {
        monitorCartState();
      }, delay);
    });
  }

  function scheduleAttributionSync(delay) {
    clearTimeout(scheduledSyncTimer);

    scheduledSyncTimer = window.setTimeout(function () {
      syncAttributionToCart().catch(function (error) {
        console.warn('[UTM Capture] Scheduled cart sync failed:', error);
      });
    }, delay || 300);
  }

  const debouncedSyncProductForms = debounce(syncUtmInputsToProductForms, 300);

  async function monitorCartState() {
    const attribution = getStoredAttribution();

    if (!attribution) {
      clearUtmInputsFromProductForms();
      await clearStaleCartAttributionAttributesIfNeeded();
      return;
    }

    try {
      const cart = await getCart();

      if (cart.item_count > 0) {
        attribution.had_items = true;
        saveAttribution(attribution);

        await syncAttributionToCart();
        return;
      }

      if (cart.item_count === 0 && attribution.had_items) {
        // Cart emptied before checkout: clear cart attributes, keep stored attribution.
        attribution.had_items = false;
        saveAttribution(attribution);

        await clearCartAttributionAttributes();
      }
    } catch (error) {
      console.warn('[UTM Capture] Cart state check failed:', error);
    }
  }

  function submitCheckoutForm(form, checkoutElement) {
    if (!form) {
      window.location.href = `${ROUTE_ROOT}checkout`;
      return;
    }

    form.__utmCaptureAllowSubmit = true;

    window.setTimeout(function () {
      form.__utmCaptureAllowSubmit = false;
    }, 3000);

    if (typeof form.requestSubmit === 'function' && checkoutElement) {
      form.requestSubmit(checkoutElement);
      return;
    }

    const hiddenCheckoutInput = document.createElement('input');
    hiddenCheckoutInput.type = 'hidden';
    hiddenCheckoutInput.name = 'checkout';
    hiddenCheckoutInput.value = 'Checkout';
    form.appendChild(hiddenCheckoutInput);

    form.submit();
  }

  async function handleCheckoutClick(event, checkoutElement) {
    const attribution = captureLatestUtmFromUrl();

    const isAnchor = checkoutElement.tagName === 'A';
    const href = isAnchor ? checkoutElement.href : null;

    const form =
      checkoutElement.form ||
      checkoutElement.closest('form') ||
      document.getElementById(checkoutElement.getAttribute('form'));

    event.preventDefault();

    try {
      if (attribution) {
        // Sync the valid snapshot to the cart; storage keeps it until it expires.
        syncUtmInputsToProductForms();
        await syncAttributionToCart();
      } else {
        // No valid attribution: remove stale cart attributes before checkout.
        await forceClearNonUtmState();
      }
    } catch (error) {
      console.warn('[UTM Capture] Checkout UTM handling failed:', error);
    }

    if (isAnchor && href) {
      window.location.href = href;
      return;
    }

    submitCheckoutForm(form, checkoutElement);
  }

  async function handleCartFormSubmit(event, form) {
    if (form.__utmCaptureAllowSubmit) {
      return;
    }

    const submitter = event.submitter || document.activeElement;

    const isCheckoutSubmitter = Boolean(
      submitter &&
      typeof submitter.matches === 'function' &&
      submitter.matches(CHECKOUT_SELECTOR)
    );

    const isCartForm = form.matches('form[action*="/cart"]');
    const hasCheckoutInput = Boolean(form.querySelector('[name="checkout"]'));

    if (!isCartForm || (!isCheckoutSubmitter && !hasCheckoutInput)) {
      return;
    }

    event.preventDefault();

    const attribution = captureLatestUtmFromUrl();

    try {
      if (attribution) {
        syncUtmInputsToProductForms();
        await syncAttributionToCart();
      } else {
        await forceClearNonUtmState();
      }
    } catch (error) {
      console.warn('[UTM Capture] Cart form submit UTM handling failed:', error);
    }

    submitCheckoutForm(form, isCheckoutSubmitter ? submitter : null);
  }

  function handleBuyNowIntent() {
    const attribution = captureLatestUtmFromUrl();

    if (attribution) {
      syncUtmInputsToProductForms();
      scheduleAttributionSync(100);
      return;
    }

    // Buy Now can skip the cart handler, so clear stale attribution here.
    forceClearNonUtmState().catch(function (error) {
      console.warn('[UTM Capture] Buy Now stale UTM cleanup failed:', error);
    });
  }

  function handleCartActionIntent() {
    const attribution = captureLatestUtmFromUrl();

    if (attribution) {
      syncUtmInputsToProductForms();
      scheduleCartStateCheck([300, 1000, 2500]);
      return;
    }

    // No valid attribution: clear stale cart attributes before the next cart action.
    forceClearNonUtmState().catch(function (error) {
      console.warn('[UTM Capture] Cart action stale UTM cleanup failed:', error);
    });

    scheduleCartStateCheck([300, 1000, 2500]);
  }

  function getRequestUrl(input) {
    if (!input) {
      return '';
    }

    if (typeof input === 'string') {
      return input;
    }

    if (input.url) {
      return input.url;
    }

    return '';
  }

  function isCartMutatingRequest(input) {
    const requestUrl = getRequestUrl(input);

    if (!requestUrl) {
      return false;
    }

    try {
      const url = new URL(requestUrl, window.location.origin);

      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch (error) {
      return /\/cart\/(add|change|update|clear)(\.js)?/.test(requestUrl);
    }
  }

  function watchFetchCartRequests() {
    if (!window.fetch || window.__utmCaptureFetchWatched) {
      return;
    }

    window.__utmCaptureFetchWatched = true;

    const originalFetch = window.fetch;

    window.fetch = function () {
      const requestInput = arguments[0];

      return originalFetch.apply(this, arguments).then(function (response) {
        if (!isInternalCartUpdate && isCartMutatingRequest(requestInput)) {
          scheduleCartStateCheck([300, 1000, 2500]);
        }

        return response;
      });
    };
  }

  function watchXhrCartRequests() {
    if (window.__utmCaptureXhrWatched) {
      return;
    }

    window.__utmCaptureXhrWatched = true;

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__utmCaptureUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      this.addEventListener('loadend', function () {
        if (!isInternalCartUpdate && isCartMutatingRequest(this.__utmCaptureUrl)) {
          scheduleCartStateCheck([300, 1000, 2500]);
        }
      });

      return originalSend.apply(this, arguments);
    };
  }

  function watchProductFormChanges() {
    if (window.__utmCaptureMutationWatched || !window.MutationObserver) {
      return;
    }

    window.__utmCaptureMutationWatched = true;

    const observer = new MutationObserver(function () {
      debouncedSyncProductForms();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function preserveAttributionBeforeAccountSubmit(form) {
    if (
      form.matches('form[action*="/account/login"]') ||
      form.matches('form[action*="/account"]') ||
      form.id === 'customer_login'
    ) {
      const attribution = getStoredAttribution();

      if (attribution) {
        saveAttribution(attribution);
        scheduleAttributionSync(0);
        return;
      }

      // Account navigation can restore a logged-in cart; clear expired attribution first.
      forceClearNonUtmState().catch(function (error) {
        console.warn('[UTM Capture] Account-submit stale UTM cleanup failed:', error);
      });
    }
  }

  function bindEvents() {
    // Buy It Now: don't block the click; UTM inputs and cart attributes are in place.
    ['pointerdown', 'mousedown', 'touchstart'].forEach(function (eventName) {
      document.addEventListener(
        eventName,
        function (event) {
          const buyNowElement = event.target.closest(BUY_NOW_SELECTOR);

          if (buyNowElement) {
            handleBuyNowIntent();
          }
        },
        true
      );
    });

    document.addEventListener(
      'click',
      function (event) {
        const checkoutElement = event.target.closest(CHECKOUT_SELECTOR);

        if (checkoutElement) {
          handleCheckoutClick(event, checkoutElement);
          return;
        }

        const buyNowElement = event.target.closest(BUY_NOW_SELECTOR);

        if (buyNowElement) {
          handleBuyNowIntent();
          return;
        }

        const cartActionElement = event.target.closest(CART_ACTION_SELECTOR);

        if (cartActionElement) {
          handleCartActionIntent();
        }
      },
      true
    );

    document.addEventListener('change', function (event) {
      const cartQuantityElement = event.target.closest(
        'input[name="updates[]"], select[name="updates[]"], [data-cart-quantity], [data-cart-update]'
      );

      if (cartQuantityElement) {
        scheduleCartStateCheck([300, 1000, 2500]);
      }
    });

    document.addEventListener('submit', function (event) {
      const form = event.target;

      if (form.__utmCaptureAllowSubmit) {
        return;
      }

      if (form.matches(PRODUCT_FORM_SELECTOR)) {
        captureLatestUtmFromUrl();
        syncUtmInputsToProductForms();
        scheduleCartStateCheck([300, 1000, 2500]);
      }

      if (form.matches('form[action*="/cart"]')) {
        handleCartFormSubmit(event, form);
        scheduleCartStateCheck([300, 1000, 2500]);
      }

      preserveAttributionBeforeAccountSubmit(form);
    });

    // Login redirects, back/forward cache, account pages, returns from checkout.
    window.addEventListener('pageshow', function () {
      captureLatestUtmFromUrl();
      syncUtmInputsToProductForms();
      scheduleCartStateCheck([300, 1000, 2500]);
    });

    window.addEventListener('focus', function () {
      syncUtmInputsToProductForms();
      scheduleCartStateCheck([300, 1000]);
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        syncUtmInputsToProductForms();
        scheduleCartStateCheck([300, 1000]);
      }
    });
  }

  function init() {
    captureLatestUtmFromUrl();
    syncUtmInputsToProductForms();

    monitorCartState();
    scheduleCartStateCheck([1000, 2500]);

    watchFetchCartRequests();
    watchXhrCartRequests();
    watchProductFormChanges();

    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ShopifyUTMCapture = {
    _installed: true,
    captureLatest: captureLatestUtmFromUrl,
    syncToCart: syncAttributionToCart,
    syncInputs: syncUtmInputsToProductForms,
    checkCartState: monitorCartState,
    clearStaleCartAttributes: function () {
      return clearStaleCartAttributionAttributesIfNeeded();
    },
    clearCartAttributes: clearCartAttributionAttributes,
    clearLineItemProperties: clearLineItemAttributionProperties,
    getStored: getStoredAttribution,
    clear: async function () {
      clearStoredAttribution();
      clearUtmInputsFromProductForms();
      await clearStaleCartAttributionAttributesIfNeeded();
    }
  };
})();
